/**
 * Wrapper for scroll-triggered animations: fades/slides children in via
 * IntersectionObserver (`useScrollAnimation`) as they enter the viewport.
 */
"use client";

import { useScrollAnimation } from "@/hooks/useScrollAnimation";

type AnimationDirection = "up" | "down" | "left" | "right" | "fade";

interface AnimatedSectionProps {
  children: React.ReactNode;
  className?: string;
  /** Delay in seconds before animation starts */
  delay?: number;
  /** Direction the element animates from */
  direction?: AnimationDirection;
  /** Custom animation duration in seconds */
  duration?: number;
}

/**
 * @example
 * <AnimatedSection direction="left" delay={0.2}>
 *   <h2>This slides in from the left</h2>
 * </AnimatedSection>
 */
export function AnimatedSection({
  children,
  className = "",
  delay = 0,
  direction = "up",
  duration = 0.6,
}: AnimatedSectionProps) {
  const { ref, isVisible } = useScrollAnimation();

  const getInitialTransform = () => {
    switch (direction) {
      case "up":
        return "translateY(40px)";
      case "down":
        return "translateY(-40px)";
      case "left":
        return "translateX(40px)";
      case "right":
        return "translateX(-40px)";
      case "fade":
      default:
        return "translateY(0)";
    }
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: isVisible ? 1 : 0,
        transform: isVisible ? "translate(0)" : getInitialTransform(),
        transition: `opacity ${duration}s ease-out ${delay}s, transform ${duration}s ease-out ${delay}s`,
      }}
    >
      {children}
    </div>
  );
}
